/**
 * Utility to check for version mismatches that can cause chunk load errors
 * This helps detect when a new deployment has occurred while users are still on old version
 */

import dayjs from 'dayjs'
import { subscriptionApi } from 'src/redux/api/rest/subscriptionApi'
import store from 'src/redux/store'

let currentVersion: string | null = null
let pollingInterval: NodeJS.Timeout | null = null
let onVersionChangeCallback: ((newVersion: string) => void) | null = null

/**
 * Check if we're running on localhost/development environment
 */
function isLocalhost(): boolean {
	if (typeof window === 'undefined') return false

	const hostname = window.location.hostname
	return (
		hostname === 'localhost' ||
		hostname === '127.0.0.1' ||
		hostname.startsWith('192.168.') ||
		hostname.endsWith('.local') ||
		hostname === '::1' // IPv6 localhost
	)
}

/**
 * Initialize version tracking with optional callback for version changes
 */
export function initVersionCheck(
	onVersionChange?: (newVersion: string) => void
) {
	// Get version from build metadata or package.json buildDate
	const buildMeta = document.querySelector('meta[name="build-version"]')
	currentVersion =
		buildMeta?.getAttribute('content') || getCurrentBuildVersion()

	// Set the callback for version changes
	onVersionChangeCallback = onVersionChange || null

	// Start polling for version changes (every 2 minutes)
	startVersionPolling()
}

/**
 * Get current build version from various sources
 */
function getCurrentBuildVersion(): string {
	try {
		// Try to get from window object if available
		if (typeof window !== 'undefined' && (window as any).__BUILD_DATE__) {
			return String((window as any).__BUILD_DATE__)
		}

		// Try to get from package.json build date if available
		if (
			typeof window !== 'undefined' &&
			(window as any).__PACKAGE_BUILD_DATE__
		) {
			return String((window as any).__PACKAGE_BUILD_DATE__)
		}

		// Fallback to timestamp
		return 'unknown'
	} catch {
		return 'unknown'
	}
}

/**
 * Start polling for version changes
 */
export function startVersionPolling() {
	// Clear any existing polling
	if (pollingInterval) {
		clearInterval(pollingInterval)
	}

	// Poll every 2 minutes
	pollingInterval = setInterval(checkForVersionChange, 2 * 60 * 1000)

	// Also check immediately
	checkForVersionChange()
}

/**
 * Stop polling for version changes
 */
export function stopVersionPolling() {
	if (pollingInterval) {
		clearInterval(pollingInterval)
		pollingInterval = null
	}
}

/**
 * Check if the server has a different version than what we're running
 */
async function checkForVersionChange() {
	try {
		// Use RTK Query subscription API to get build info
		const result = await store.dispatch(
			subscriptionApi.endpoints.fetchBuildInfo.initiate(undefined)
		)

		if ('data' in result && result.data?.buildDate) {
			const response = result.data
			const serverVersion = String(response.buildDate)

			// Parse build dates as timestamps and calculate the difference
			const serverDate = dayjs(Number(response.buildDate))
			const localDate = dayjs(Number(currentVersion))
			const timeDiffInMinutes = Math.abs(
				serverDate.diff(localDate, 'minute')
			)

			if (
				currentVersion &&
				serverVersion &&
				currentVersion !== serverVersion &&
				serverVersion !== 'unknown' &&
				timeDiffInMinutes > 10
			) {
				// Version mismatch detected and more than 30 minutes apart
				console.warn(
					'Version mismatch detected. Current:',
					currentVersion,
					'Server:',
					serverVersion,
					'Time difference (minutes):',
					timeDiffInMinutes,
					'Branch:',
					response.branch,
					'Commit:',
					response.commitId
				)

				// Skip version mismatch notifications on localhost
				if (!isLocalhost()) {
					// Call the callback if provided (for toast notification)
					if (onVersionChangeCallback) {
						onVersionChangeCallback(serverVersion)
					}
				} else {
					console.debug(
						'Version mismatch detected on localhost - ignoring notification'
					)
				}

				// Update current version to prevent repeated notifications
				currentVersion = serverVersion
			} else if (
				currentVersion &&
				serverVersion !== currentVersion &&
				timeDiffInMinutes <= 30
			) {
				console.debug(
					'Version mismatch detected but within 30 minute threshold:',
					{
						local: currentVersion,
						server: serverVersion,
						timeDifferenceMinutes: timeDiffInMinutes,
					}
				)
			}
		}
	} catch (error) {
		// Silently fail - network might be down or API unavailable
		console.debug('Version check failed:', error)

		// Fallback to old method if subscription API fails
		try {
			const response = await fetch(`/meta.json?t=${Date.now()}`, {
				cache: 'no-cache',
				headers: {
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache',
				},
			})

			if (response.ok) {
				const serverMeta = await response.json()
				const serverVersion = String(
					serverMeta.buildDate || serverMeta.version || 'unknown'
				)

				// Parse build dates as timestamps and calculate the difference for fallback too
				const serverDate = dayjs(Number(serverVersion))
				const localDate = dayjs(Number(currentVersion))
				const timeDiffInMinutes = Math.abs(
					serverDate.diff(localDate, 'minute')
				)

				if (
					currentVersion &&
					serverVersion &&
					currentVersion !== serverVersion &&
					serverVersion !== 'unknown' &&
					timeDiffInMinutes > 30
				) {
					console.warn(
						'Version mismatch detected (fallback). Current:',
						currentVersion,
						'Server:',
						serverVersion,
						'Time difference (minutes):',
						timeDiffInMinutes
					)

					// Skip version mismatch notifications on localhost
					if (!isLocalhost()) {
						if (onVersionChangeCallback) {
							onVersionChangeCallback(serverVersion)
						}
					} else {
						console.debug(
							'Version mismatch detected on localhost (fallback) - ignoring notification'
						)
					}

					currentVersion = serverVersion
				} else if (
					currentVersion &&
					serverVersion !== currentVersion &&
					timeDiffInMinutes <= 30
				) {
					console.debug(
						'Version mismatch detected (fallback) but within 30 minute threshold:',
						{
							local: currentVersion,
							server: serverVersion,
							timeDifferenceMinutes: timeDiffInMinutes,
						}
					)
				}
			}
		} catch (fallbackError) {
			console.debug('Fallback version check also failed:', fallbackError)
		}
	}
}

/**
 * Force refresh when chunk loading fails
 */
export function handleChunkLoadError() {
	console.warn('Chunk load error detected, refreshing to get latest version')

	// Clear any cached data that might be causing issues
	if ('caches' in window) {
		caches.keys().then((names) => {
			names.map((name) => caches.delete(name))
		})
	}

	// Force refresh
	window.location.reload()
}
